import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@west-tennessee-consulting/ui";
import { CompanySelectWithModal } from "@/components/ui/selects/components/CompanySelectWithModal";
import { ClientSelectWithModal } from "@/components/ui/selects/components/ClientSelectWithModal";
import { TicketSelectWithModal } from "@/components/ui/selects/components/TicketSelectWithModal";
import { UserSelect } from "@/components/shared/UserSelect";
import { MultiUserSelect } from "@/components/shared/selects/MultiUserSelect";
import { EmailAliasesEditor } from "@/features/contacts/components/EmailAliasesEditor";
import { JsonFieldEditor } from "@/components/shared/JsonFieldEditor";
import { CalendarDateEditModal } from "@/components/shared/UniversalCRMTable/components/EditableCellInputs/inputs/CalendarDateEditModal";
import { normalizeMultiUserData } from "./field-helpers";
import type { SimpleFieldConfig, FieldValue } from "../types";
import type { JsonFieldValue } from "@/components/shared/types";

export interface RenderFieldInputProps {
  fieldId: string;
  fieldConfig: SimpleFieldConfig;
  value: FieldValue;
  onValueChange?: (value: any) => void;
  placeholder?: string;
  fullWidth?: boolean;
  autoFocus?: boolean;
  /** When true, user/assignment selects expand inline instead of popover (e.g. in modals) */
  expandInline?: boolean;
  /** Optional keyboard event handler for Enter key to save */
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

/**
 * Renders a field input for editing (edit mode)
 * Handles all field types with appropriate input controls
 */
export const renderFieldInput = ({
  fieldId,
  fieldConfig,
  value,
  onValueChange,
  placeholder,
  fullWidth = false,
  autoFocus = false,
  expandInline = false,
  onKeyDown,
}: RenderFieldInputProps): React.ReactNode => {
  const fieldType = fieldConfig?.type || "text";
  const referencedTable = fieldConfig?.isForeignKey ? fieldConfig.foreignKey?.referencedTable : null;
  const referencedColumn = fieldConfig?.isForeignKey ? fieldConfig.foreignKey?.referencedColumn : null;
  const foreignKeyTarget = referencedTable && referencedColumn ? `${referencedTable}.${referencedColumn}` : null;

  // Special handling for ticket fields
  const isTicketField =
    foreignKeyTarget === "tickets.id" ||
    fieldType === "select" && (fieldId === "ticket_id" || fieldId?.includes("ticket"));

  if (isTicketField) {
    return (
      <div className="relative">
        <TicketSelectWithModal
          value={String(value || "")}
          onValueChange={onValueChange!}
          placeholder={placeholder || "Select ticket..."}
          fullWidth={fullWidth}
          highZIndex={true}
          expandInline={true}
          expandInlineVariant="replace"
        />
      </div>
    );
  }

  // Special handling for company fields
  const normalizedFieldId = fieldId.toLowerCase();
  const isCompanyField =
    foreignKeyTarget === "companies.id" ||
    normalizedFieldId === "company_id" ||
    normalizedFieldId === "company" ||
    normalizedFieldId === "companyid" ||
    (normalizedFieldId.includes("company") && !normalizedFieldId.includes("name"));

  if (isCompanyField) {
    return (
      <div className="relative">
        <CompanySelectWithModal
          value={String(value || "")}
          onValueChange={onValueChange!}
          placeholder={placeholder || "Select company..."}
          highZIndex={true}
          expandInline={true}
          expandInlineVariant="replace"
        />
      </div>
    );
  }

  // Special handling for contact fields
  const isContactField = fieldId === "contact_id" || fieldId?.includes("contact");
  const isMetadataContactFk = foreignKeyTarget === "contacts.id";

  if (isContactField || isMetadataContactFk) {
    return (
      <div className="relative">
        <ClientSelectWithModal
          value={String(value || "")}
          onValueChange={onValueChange!}
          placeholder={placeholder || "Select contact..."}
          highZIndex={true}
          expandInline={true}
          expandInlineVariant="replace"
        />
      </div>
    );
  }

  // Multi-user assignments (assigned_users, engineer_ids) — must run before generic "assign" UserSelect match
  if (fieldType === "multi-user") {
    const normalizedValue = normalizeMultiUserData(value);
    return (
      <div className="relative">
        <MultiUserSelect
          currentUserIds={normalizedValue}
          onUsersSelect={async (userIds: string[]) => onValueChange?.(userIds)}
          placeholder={placeholder || "Select users..."}
          expandInline={true}
          expandInlineVariant="replace"
        />
      </div>
    );
  }

  // Special handling for user fields
  const isUserField =
    foreignKeyTarget === "app_users.id" ||
    fieldId === "user_id" ||
    fieldId === "assigned_to" ||
    fieldId?.includes("user") ||
    fieldId?.includes("assign");

  if (isUserField) {
    return (
      <div className="relative">
        <UserSelect
          currentUserId={String(value || "")}
          onUserSelect={async (userId: string) => onValueChange?.(userId)}
          showUnassigned={true}
          showAllUsers={false}
          placeholder={placeholder || "Select user"}
          className="w-full"
          fullWidth={fullWidth}
          expandInline={expandInline}
          expandInlineVariant="replace"
        />
      </div>
    );
  }

  // Special handling for custom components
  if (fieldType === "custom" && fieldId === "email_aliases") {
    // For email_aliases, we need the full contact object
    // This will be handled by the cell rendering logic that passes the contact
    return (
      <div className="text-sm text-muted-foreground">Use the email editor to manage aliases</div>
    );
  }

  if (fieldType === "custom" && fieldId === "phone_aliases") {
    return (
      <div className="text-sm text-muted-foreground">Use the phone editor to manage aliases</div>
    );
  }

  // Default field type handling
  switch (fieldType) {
    case "text":
      return (
        <Input
          value={String(value || "")}
          onChange={(e) => onValueChange?.(e.target.value)}
          onKeyDown={onKeyDown}
          className="w-full"
          autoFocus={autoFocus}
          placeholder={placeholder}
        />
      );

    case "textarea":
      return (
        <Textarea
          value={String(value || "")}
          onChange={(e) => onValueChange?.(e.target.value)}
          onKeyDown={onKeyDown}
          className="w-full min-h-[120px] resize-none"
          placeholder={placeholder || "Enter description..."}
          autoFocus={autoFocus}
        />
      );

    case "select":
      const options = fieldConfig?.options || [];
      return (
        <Select value={String(value || "")} onValueChange={onValueChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={placeholder || `Select ${fieldConfig?.label}`} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case "number":
      return (
        <Input
          type="number"
          value={String(value || "")}
          onChange={(e) => onValueChange?.(parseFloat(e.target.value) || 0)}
          onKeyDown={onKeyDown}
          className="w-full"
          autoFocus={autoFocus}
          placeholder={placeholder}
          min={fieldConfig?.min}
          max={fieldConfig?.max}
        />
      );

    case "date":
    case "due-date":
      return (
        <CalendarDateEditModal
          value={value ? String(value) : undefined}
          onValueChange={onValueChange!}
          placeholder={placeholder || "Select date..."}
          autoFocus={autoFocus}
        />
      );

    case "tags":
      return (
        <Input
          value={Array.isArray(value) ? value.join(", ") : String(value || "")}
          onChange={(e) => {
            const tagArray = e.target.value
              .split(",")
              .map((tag) => tag.trim())
              .filter(Boolean);
            onValueChange?.(tagArray);
          }}
          onKeyDown={onKeyDown}
          className="w-full"
          placeholder={placeholder || "Enter tags separated by commas"}
          autoFocus={autoFocus}
        />
      );

    case "boolean":
      return (
        <Select
          value={String(value || "false")}
          onValueChange={(val) => onValueChange?.(val === "true")}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={placeholder || "Select option"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">Yes</SelectItem>
            <SelectItem value="false">No</SelectItem>
          </SelectContent>
        </Select>
      );

    case "json":
      return (
        <JsonFieldEditor
          value={value as JsonFieldValue}
          fieldId={fieldId}
          onSave={async (newValue) => onValueChange?.(newValue)}
          readOnlyMode={false}
        />
      );

    default:
      return (
        <Input
          value={String(value || "")}
          onChange={(e) => onValueChange?.(e.target.value)}
          onKeyDown={onKeyDown}
          className="w-full"
          autoFocus={autoFocus}
          placeholder={placeholder}
        />
      );
  }
};
